import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AlertTriangle, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

const TYPE_LABEL = {
  statutory: "Statutory / Regulatory",
  australian_standard: "Australian Standard",
  manufacturer: "Manufacturer",
  insurance: "Insurance",
  lease: "Lease",
  internal: "Internal SOP",
  good_practice: "Good practice",
};

export default function Compliance() {
  const [params, setParams] = useSearchParams();
  const filter = params.get("filter") || "all";
  const [rows, setRows] = useState([]);
  const { user } = useAuth();

  const load = useCallback(async () => {
    const p = filter === "all" ? {} : { filter };
    const { data } = await api.get("/compliance", { params: p });
    setRows(data);
  }, [filter]);
  useEffect(() => { load();  }, [load]);

  const verify = async (id) => {
    try { await api.post(`/compliance/${id}/verify`); toast.success("Verified"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="compliance-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500">Register</div>
        <h1 className="font-heading text-3xl font-bold text-slate-900">Compliance obligations</h1>
        <p className="text-sm text-slate-600 mt-1 max-w-3xl">
          Sourced from the workbook’s standards, references and asset compliance columns. Every item is flagged
          <span className="mx-1 inline-flex items-center gap-1 text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-0.5 text-xs"><AlertTriangle className="w-3 h-3"/>Verification required</span>
          until confirmed by a qualified contractor, regulator or adviser.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {[["all","All"],["overdue","Overdue"],["due_30","Due 30 days"],["expiring","Expiring certificates"]].map(([k,label])=>(
          <button key={k} data-testid={`compliance-filter-${k}`}
            onClick={()=>setParams(k==="all"?{}:{filter:k})}
            className={`px-3 py-1.5 text-xs rounded-full border transition-colors duration-200 ${filter===k?"bg-slate-900 text-white border-slate-900":"bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}>{label}</button>
        ))}
      </div>

      <Card className="border-slate-200"><CardContent className="p-4">
        <div className="border border-slate-200 rounded-md overflow-hidden">
          <Table className="dense-table">
            <TableHeader><TableRow>
              <TableHead>Category</TableHead>
              <TableHead>Requirement</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Standard / Ref</TableHead>
              <TableHead>Next due</TableHead>
              <TableHead>Certificate expiry</TableHead>
              <TableHead>Verified</TableHead>
              <TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((c)=>(
                <TableRow key={c.id} data-testid={`compliance-row-${c.id}`}>
                  <TableCell className="text-slate-700 text-sm">{c.category}</TableCell>
                  <TableCell><div className="text-sm text-slate-900 max-w-md">{c.requirement}</div></TableCell>
                  <TableCell><Badge variant="outline" className="text-xs">{TYPE_LABEL[c.requirement_type] || c.requirement_type}</Badge></TableCell>
                  <TableCell className="text-xs text-slate-600 max-w-xs">{c.standard_ref || "—"}</TableCell>
                  <TableCell className="font-mono text-xs">{c.next_due_date ? new Date(c.next_due_date).toLocaleDateString() : "—"}</TableCell>
                  <TableCell className="font-mono text-xs">{c.certificate_expiry_date ? new Date(c.certificate_expiry_date).toLocaleDateString() : "—"}</TableCell>
                  <TableCell>
                    {c.verified
                      ? <Badge className="bg-emerald-100 text-emerald-800 border-0"><ShieldCheck className="w-3 h-3 mr-1"/>Verified</Badge>
                      : <Badge className="bg-amber-50 text-amber-800 border border-amber-200">Verification required</Badge>}
                  </TableCell>
                  <TableCell className="text-right">
                    {!c.verified && user?.role === "admin" && (
                      <Button size="sm" variant="outline" onClick={()=>verify(c.id)} data-testid={`compliance-verify-${c.id}`}>Mark verified</Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {rows.length===0 && <TableRow><TableCell colSpan={8} className="text-center text-slate-500 py-8">No compliance items match.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </CardContent></Card>
    </div>
  );
}
